import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Wordmark } from '@keobook/ui';
import { useAuth } from '../lib/auth';
import { ChatPanel } from './ChatPanel';

const PLAN_LABEL: Record<string, string> = {
  starter: 'Starter',
  professional_plus: 'Professional+',
  business_plus: 'Business+',
};

/**
 * Authenticated app shell: top bar with brand + current workspace (company)
 * on the left, user chip with sign-out on the right.
 */
export function Layout({ children }: { children: ReactNode }) {
  const { user, clients, currentClient, setCurrentClient, signOut, isDemo, exitDemo } = useAuth();
  return (
    <div className="kb-app">
      {isDemo && (
        <div className="kb-demo-banner" role="status" aria-live="polite">
          <span>
            <strong>Demo mode.</strong> You are exploring sample books — nothing is saved beyond
            this session.
          </span>
          <a
            href={
              (import.meta.env.VITE_MARKETING_URL as string | undefined) ?? 'https://keobooks.com'
            }
            className="kb-demo-banner__cta"
          >
            Sign up to keep your books →
          </a>
          <button type="button" className="kb-demo-banner__exit" onClick={exitDemo}>
            Exit demo
          </button>
        </div>
      )}
      <header className="kb-topbar">
        <div className="kb-topbar__left">
          <Wordmark size="sm" />
          {currentClient && (
            <>
              <span className="kb-topbar__divider" aria-hidden="true" />
              {clients.length > 1 ? (
                // Accountants linked to several businesses can switch here.
                <span className="kb-workspace">
                  <select
                    aria-label="Switch business"
                    className="kb-workspace__switcher"
                    value={currentClient.id}
                    onChange={(e) => {
                      setCurrentClient(e.currentTarget.value);
                      // Reload into the selected business so every page's data
                      // re-fetches under the new X-Client-Id.
                      window.location.assign('/dashboard');
                    }}
                    style={{
                      font: 'inherit',
                      fontWeight: 600,
                      color: 'inherit',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.displayName}
                      </option>
                    ))}
                  </select>
                  <span className="kb-workspace__plan">
                    {PLAN_LABEL[currentClient.planTier] ?? ''}
                  </span>
                </span>
              ) : (
                <span className="kb-workspace">
                  <span className="kb-workspace__name">{currentClient.displayName}</span>
                  <span className="kb-workspace__plan">
                    {PLAN_LABEL[currentClient.planTier] ?? ''}
                  </span>
                </span>
              )}
            </>
          )}
        </div>
        {currentClient && (
          <nav className="kb-nav">
            <NavLink
              to="/dashboard"
              className={({ isActive }) => `kb-nav__link ${isActive ? 'is-active' : ''}`}
            >
              Dashboard
            </NavLink>
            <NavLink
              to="/documents"
              className={({ isActive }) => `kb-nav__link ${isActive ? 'is-active' : ''}`}
            >
              Documents
            </NavLink>
            <NavLink
              to="/activity"
              className={({ isActive }) => `kb-nav__link ${isActive ? 'is-active' : ''}`}
            >
              Activity
            </NavLink>
            <NavLink
              to="/ledger"
              className={({ isActive }) => `kb-nav__link ${isActive ? 'is-active' : ''}`}
            >
              Ledger
            </NavLink>
            <NavLink
              to="/invoicing"
              className={({ isActive }) => `kb-nav__link ${isActive ? 'is-active' : ''}`}
            >
              Invoicing
            </NavLink>
            <NavLink
              to="/reports"
              className={({ isActive }) => `kb-nav__link ${isActive ? 'is-active' : ''}`}
            >
              Reports
            </NavLink>
            <NavLink
              to="/periods"
              className={({ isActive }) => `kb-nav__link ${isActive ? 'is-active' : ''}`}
            >
              Periods
            </NavLink>
            <NavLink
              to="/accounts"
              className={({ isActive }) => `kb-nav__link ${isActive ? 'is-active' : ''}`}
            >
              Accounts
            </NavLink>
            <NavLink
              to="/referral"
              className={({ isActive }) => `kb-nav__link ${isActive ? 'is-active' : ''}`}
            >
              Refer &amp; earn
            </NavLink>
            {user?.kind === 'staff' && (
              <NavLink
                to="/admin"
                className={({ isActive }) => `kb-nav__link ${isActive ? 'is-active' : ''}`}
              >
                Admin
              </NavLink>
            )}
          </nav>
        )}
        <div className="kb-topbar__right">
          <Link to="/settings" className="kb-iconlink" title="Settings" aria-label="Settings">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
            </svg>
          </Link>
          <button
            type="button"
            className="kb-user-chip"
            onClick={signOut}
            title="Sign out"
            aria-label={`Sign out (${user?.email ?? ''})`}
          >
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
            <span>{user?.email ?? 'Signed in'}</span>
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
          </button>
        </div>
      </header>
      <main>{children}</main>
      {/* Customer chat — floating button bottom-right that expands into a
          side panel. Hidden in demo mode (the assistant grounds against the
          live engagement; demo's read-only sample books don't have one). */}
      {!isDemo && currentClient && <ChatPanel />}
    </div>
  );
}
